import React, { useState, useEffect } from 'react';
import './TraitFilterBar.css';

function TraitFilterBar({ nfts, onFilterChange, resetKey, enablePriceFilter }) {
    // Initialize sort options conditionally based on enablePriceFilter prop.
    const initialSortOptions = enablePriceFilter
        ? { id: true, rarity: false, priceAsc: false, priceDesc: false }
        : { id: true, rarity: false };

    const [filters, setFilters] = useState({});
    const [isExpanded, setIsExpanded] = useState(false);
    const [filteredCount, setFilteredCount] = useState(nfts.length);
    const [sortOptions, setSortOptions] = useState(initialSortOptions);
    const [idSearch, setIdSearch] = useState('');

    useEffect(() => {
        let toShow = nfts;

        const activeFilters = getActiveFilters();
        if (Object.keys(activeFilters).length) {
            toShow = toShow.filter(nft =>
                Object.entries(activeFilters).every(([type, values]) =>
                    values.some(
                        value =>
                            nft.traits &&
                            nft.traits.some(
                                trait => trait.trait_type === type && trait.value === value
                            )
                    )
                )
            );
        }

        if (idSearch.trim() !== '') {
            toShow = toShow.filter(nft =>
                String(nft.token_id).includes(idSearch)
            );
        }

        setFilteredCount(toShow.length);
        onFilterChange(activeFilters, getCurrentSortOption(), idSearch); // Optionally pass idSearch
        // eslint-disable-next-line
    }, [idSearch, filters, nfts]);

    useEffect(() => {
        // Reset all filters
        const traits = {};
        nfts.forEach((nft) => {
            nft.traits.forEach((trait) => {
                if (!traits[trait.trait_type]) {
                    traits[trait.trait_type] = new Set();
                }
                traits[trait.trait_type].add(trait.value);
            });
        });

        const initialFilters = {};
        for (const type in traits) {
            initialFilters[type] = Array.from(traits[type])
                .sort((a, b) => a.localeCompare(b))
                .map((value) => ({
                    value,
                    checked: false,
                }));
        }

        setFilters(initialFilters);
        setFilteredCount(nfts.length);
        setSortOptions(initialSortOptions);
        setIdSearch('');
        setIsExpanded(false); // Optionally collapse filters on tab switch
        onFilterChange({}, getCurrentSortOption(), '');
        // eslint-disable-next-line
    }, [resetKey, nfts]);

    const handleCheckboxChange = (type, value) => {
        const updatedFilters = { ...filters };
        const filter = updatedFilters[type].find((f) => f.value === value);
        filter.checked = !filter.checked;
        setFilters(updatedFilters);

        const activeFilters = {};
        Object.entries(updatedFilters).forEach(([traitType, traitValues]) => {
            const activeValues = traitValues.filter((f) => f.checked).map((f) => f.value);
            if (activeValues.length > 0) {
                activeFilters[traitType] = activeValues;
            }
        });

        const filteredNfts = nfts.filter((nft) =>
            Object.entries(activeFilters).every(([type, values]) =>
                values.some((value) =>
                    nft.traits.some((trait) => trait.trait_type === type && trait.value === value)
                )
            )
        );

        setFilteredCount(filteredNfts.length);
        onFilterChange(activeFilters, getCurrentSortOption());
    };

    const handleSortOptionChange = (option) => {
        setSortOptions(() => {
            // Set all sort options to false, then mark the chosen one true.
            const updated = {};
            // Build updated options based on whether price filtering is enabled.
            if (enablePriceFilter) {
                updated.id = false;
                updated.rarity = false;
                updated.priceAsc = false;
                updated.priceDesc = false;
            } else {
                updated.id = false;
                updated.rarity = false;
            }
            updated[option] = true;
            // Call onFilterChange with the active filters and new sort option.
            const activeFilters = getActiveFilters();
            onFilterChange(activeFilters, option);
            return updated;
        });
    };

    const getActiveFilters = () => {
        const activeFilters = {};
        Object.entries(filters).forEach(([traitType, traitValues]) => {
            const activeValues = traitValues.filter((f) => f.checked).map((f) => f.value);
            if (activeValues.length > 0) {
                activeFilters[traitType] = activeValues;
            }
        });
        return activeFilters;
    };

    const getCurrentSortOption = () => {
        if (enablePriceFilter) {
            if (sortOptions.priceAsc) return 'priceAsc';
            if (sortOptions.priceDesc) return 'priceDesc';
        }
        if (sortOptions.rarity) return 'rarity';
        return 'id';
    };

    return (
        <div className="trait-filter-bar-container">
            <div className="trait-filter-controls">
                <button className="toggle-button" onClick={() => setIsExpanded(!isExpanded)}>
                    {isExpanded ? 'Hide Filters' : 'Show Filters'}
                </button>
                <div className="nft-count">NFT Count: {filteredCount}</div>
                <input
                    type="text"
                    className="nft-id-search"
                    placeholder="Search NFT ID"
                    value={idSearch}
                    onChange={e => setIdSearch(e.target.value.replace(/\D/g, ''))} // digits only, optional
                    style={{ width: '110px', fontSize: '14px', padding: '6px', borderRadius: '5px', border: '1px solid #00ccff', marginLeft: '12px' }}
                />
                
            </div>
            {isExpanded && (
                <div className="trait-filter-bar">
                    {Object.entries(filters).map(([type, values]) => (
                        <div key={type} className="filter-section">
                            <h4>{type}</h4>
                            <ul>
                                {values.map(({ value, checked }) => (
                                    <li key={value}>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => handleCheckboxChange(type, value)}
                                            />
                                            {value}
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default TraitFilterBar;
